import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getLocationsList,
  getProductStateList,
  getProductsList,
  productsByUser,
  transferProduct,
  transportProduct,
  updateProductDB,
  getProductLotList,
  archiveLot,
  unarchiveLot
} from '../Redux/products/slice';
import { saveProperties, saveDraftProperties, deleteDraftProperties } from '../Redux/properties/PropertiesSlice';
import { FormattedMessage, useIntl } from 'react-intl';
import moment from 'moment';
import { 
  Tabs, 
  Group, 
  TextInput, 
  ActionIcon, 
  Button, 
  Table, 
  Card, 
  Text, 
  Badge, 
  Menu, 
  Tooltip, 
  Paper, 
  ScrollArea, 
  Box, 
  Title, 
  Center, 
  Divider,
  Stack,
  SegmentedControl,
  Grid,
  Avatar,
  Pagination
} from '@mantine/core';

// Instead of using @mantine/modals, we'll use a standard modal from your existing components
// Assuming you have RemoveFieldConfirmationModal or similar that we can reuse
import { 
  IconRefresh, 
  IconPlus, 
  IconSearch, 
  IconFilter, 
  IconEye, 
  IconEdit, 
  IconArrowsLeftRight, 
  IconTruckDelivery, 
  IconArchive, 
  IconTrash,
  IconFileText,
  IconDownload,
  IconDotsVertical,
  IconChevronDown,
  IconPhoto,
  IconLayoutList,
  IconLayoutGrid
} from '@tabler/icons-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf } from '@fortawesome/free-solid-svg-icons';
import { BASE_URL } from '../Redux/apiConfig';
import { toast } from 'react-toastify';
import { ToastProperties } from '../utils/constants/toastProperties';

// Import your existing modals
import ProductPanel from './ProductPanel';
import TransportationModal from './TransportationModal';
import LoadImageModal from '../components/LoadImageModal';
import RemoveFieldConfirmationModal from '../components/RemoveFieldConfirmationModal';
import CreatePropertiesModal from '../components/CreatePropertiesModal';

// Constants for product states
const PRODUCT_STATES = {
  ACTIVE: 'active',
  DRAFT: 'draft',
  ARCHIVED: 'archived'
};

const VIEW_MODES = {
  TABLE: 'table',
  CARDS: 'cards'
};

export default function ImprovedProductManagement() {
  const dispatch = useDispatch();
  const intl = useIntl();
  
  // Redux state
  const dataLot = useSelector((state) => state.products.productLotList);
  const locationList = useSelector((state) => state.products.locationsList);
  const productsList = useSelector((state) => state.products.productsList);
  const selectedLanguage = useSelector((state) => state.language);
  
  // Component state
  const [activeTab, setActiveTab] = useState(PRODUCT_STATES.ACTIVE);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState(VIEW_MODES.TABLE);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  // Modals state
  const [showProductPanel, setShowProductPanel] = useState(false);
  const [showTransportModal, setShowTransportModal] = useState(false);
  const [showLoadImageModal, setShowLoadImageModal] = useState(false);
  const [showSellConfirmationModal, setShowSellConfirmationModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showUnarchiveModal, setShowUnarchiveModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modalActionData, setModalActionData] = useState(null);
  
  // Selected data
  const [selectedData, setSelectedData] = useState(null);
  const [transportData, setTransportData] = useState(null);
  const [transportMode, setTransportMode] = useState('');
  
  const userinfo = JSON.parse(localStorage.getItem('userinfo'));
  
  // Fetch data on component mount
  useEffect(() => {
    dispatch(productsByUser());
    dispatch(getProductsList());
    dispatch(getLocationsList());
    dispatch(getProductStateList());
    dispatch(getProductLotList());
  }, [dispatch]);
  
  // Refresh data when language changes
  useEffect(() => {
    dispatch(getProductLotList());
  }, [dispatch, selectedLanguage]);
  
  // Filter products based on active tab and search
  const getFilteredProducts = () => {
    let filteredProducts = [...dataLot];
    
    // Filter by tab
    switch (activeTab) {
      case PRODUCT_STATES.DRAFT:
        filteredProducts = filteredProducts.filter(product => product.state === 'Draft');
        break;
      case PRODUCT_STATES.ARCHIVED:
        filteredProducts = filteredProducts.filter(product => product.publishId === -1);
        break;
      case PRODUCT_STATES.ACTIVE:
      default:
        filteredProducts = filteredProducts.filter(product => 
          product.state !== 'Draft' && product.publishId !== -1);
        break;
    }
    
    // Filter by search term
    if (search) {
      const searchTerm = search.toLowerCase().trim();
      filteredProducts = filteredProducts.filter(product => {
        return Object.values(product).some(value => {
          if (typeof value === 'string') {
            return value.toLowerCase().includes(searchTerm);
          } else if (typeof value === 'number' || typeof value === 'bigint') {
            return value.toString().includes(searchTerm);
          }
          return false;
        });
      });
    }
    
    return filteredProducts;
  };
  
  // Pagination logic
  const paginatedProducts = () => {
    const filteredProducts = getFilteredProducts();
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredProducts.slice(startIndex, startIndex + itemsPerPage);
  };
  
  // Action handlers
  const handleRefresh = () => {
    dispatch(getProductLotList());
    toast.info(intl.formatMessage({ id: 'notifications.refreshed.message' }), {
      ...ToastProperties
    });
  };
  
  const handleView = (data) => {
    setSelectedData(data);
    setShowProductPanel(true);
  };
  
  const handleEdit = (data) => {
    setSelectedData(data);
    setShowCreateModal(true);
  };
  
  const handleLoadImage = (data) => {
    setSelectedData(data);
    setShowLoadImageModal(true);
  };
  
  const handleTransport = (data) => {
    const locationId = locationList.find(state => state.location === data.location)?.id || null;
    
    const payload = {
      contractID: userinfo.product_contractId,
      sku: data.sku,
      productLotUuid: data.productID,
      location: data.location,
      period: data.period,
      locationId: locationId,
      product_ref_id: data.product_ref_id,
    };
    
    setTransportData(payload);
    setTransportMode('transport');
    setShowTransportModal(true);
  };
  
  const handleTransform = (data) => {
    const locationId = locationList.find(state => state.location === data.location)?.id || null;
    const id_category = productsList.find(state => state.label === data.productName)?.id_category || null;
    
    const payload = {
      contractID: userinfo.product_contractId,
      sku: data.sku,
      productLotUuid: data.productID,
      physicalState: data.physicalState,
      location: data.location,
      period: data.period,
      categoryName: data.productName,
      locationId: locationId,
      category: id_category,
      product_ref_id: data.product_ref_id,
    };
    
    setTransportData(payload);
    setTransportMode('transform');
    setShowTransportModal(true);
  };
  
  const handlePublishChange = (data) => {
    setSelectedData(data);
    
    if (data.publish === 'N') {
      setShowSellConfirmationModal(true);
    } else {
      publishProduct(data);
    }
  };
  
  const publishProduct = (data) => {
    const obj = {
      ...data,
      contractId: userinfo.product_contractId,
      upc: data.UPC,
      publish: data.publish === 'Y' ? 'N' : 'Y',
      state: data.publishId === 0 ? 'ForSale' : 'NotForSale',
    };
    
    dispatch(updateProductDB(obj));
  };
  
  const handleArchive = (data) => {
    setModalActionData(data);
    setShowArchiveModal(true);
  };
  
  const confirmArchive = () => {
    if (modalActionData) {
      const obj = {
        ...modalActionData,
        contractId: userinfo.product_contractId,
        upc: modalActionData.UPC,
        publishId: -1,
        publish: modalActionData.publish,
        state: 'Archived'
      };
      dispatch(archiveLot(obj));
      setShowArchiveModal(false);
    }
  };
  
  const handleUnarchive = (data) => {
    setModalActionData(data);
    setShowUnarchiveModal(true);
  };
  
  const confirmUnarchive = () => {
    if (modalActionData) {
      const obj = {
        ...modalActionData,
        contractId: userinfo.product_contractId,
        upc: modalActionData.UPC,
        publishId: 0,
        publish: 'N',
        state: 'NotForSale'
      };
      dispatch(unarchiveLot(obj));
      setShowUnarchiveModal(false);
    }
  };
  
  const handleDelete = (data) => {
    setModalActionData(data);
    setShowDeleteModal(true);
  };
  
  const confirmDelete = () => {
    if (modalActionData) {
      dispatch(deleteDraftProperties(modalActionData));
      setShowDeleteModal(false);
    }
  };
  
  const downloadCertificate = async (productId) => {
    try {
      const productResponse = await fetch(`${BASE_URL}/getProductCertificate?product_id=${productId}&targetLanguage=${selectedLanguage}`);
      
      if (!productResponse.ok) {
        throw new Error('Error fetching product certificate');
      }
      
      const certificateResponse = await fetch(`${BASE_URL}/certificateContent?product_id=${productId}`);
      
      if (certificateResponse.ok) {
        window.open(certificateResponse.url, '_blank');
      } else {
        throw new Error('Error fetching PDF');
      }
    } catch (error) {
      console.error('Error:', error.message);
      toast.error(intl.formatMessage({ id: 'toast.certificate.error' }), {
        ...ToastProperties
      });
    }
  };
  
  const onSave = (obj, cb) => {
    try {
      dispatch(saveProperties({ payload: obj, cb }));
      toast.success(intl.formatMessage({ id: 'toast.product.saved' }), {
        ...ToastProperties
      });
    } catch (error) {
      toast.error(intl.formatMessage({ id: 'toast.product.error' }), {
        ...ToastProperties
      });
    }
  };
  
  const onDraftSave = (obj, cb) => {
    try {
      dispatch(saveDraftProperties({ payload: obj, cb }));
      toast.success(intl.formatMessage({ id: 'toast.draft.saved' }), {
        ...ToastProperties
      });
    } catch (error) {
      toast.error(intl.formatMessage({ id: 'toast.draft.error' }), {
        ...ToastProperties
      });
    }
  };
  
  // Render status badge
  const renderStatusBadge = (data) => {
    if (data.publishId === -1) {
      return <Badge color="gray"><FormattedMessage id="status.archived" /></Badge>;
    }
    
    if (data.state === 'Draft') {
      return <Badge color="blue"><FormattedMessage id="status.draft" /></Badge>;
    }
    
    return data.publish === 'Y' 
      ? <Badge color="green"><FormattedMessage id="status.forSale" /></Badge>
      : <Badge color="yellow"><FormattedMessage id="status.notForSale" /></Badge>;
  };
  
  // Render primary action button based on product state
  const renderPrimaryAction = (data) => {
    if (data.publishId === -1) {
      return (
        <Button 
          variant="outline" 
          color="blue" 
          size="xs" 
          leftIcon={<IconArchive size={14} />}
          onClick={() => handleUnarchive(data)}
        >
          <FormattedMessage id="action.unarchive" />
        </Button>
      );
    }
    
    if (data.state === 'Draft') {
      return (
        <Button 
          variant="outline" 
          color="blue" 
          size="xs" 
          leftIcon={<IconEdit size={14} />}
          onClick={() => handleEdit(data)}
        >
          <FormattedMessage id="action.edit" />
        </Button>
      );
    }
    
    return (
      <Button 
        variant={data.publish === 'Y' ? 'outline' : 'filled'} 
        color={data.publish === 'Y' ? 'orange' : 'green'} 
        size="xs" 
        onClick={() => handlePublishChange(data)}
      >
        {data.publish === 'Y' 
          ? <FormattedMessage id="action.withdraw" />
          : <FormattedMessage id="action.publish" />
        }
      </Button>
    );
  };
  
  // Render action menu for each product
  const renderActionMenu = (data) => {
    const isArchived = data.publishId === -1;
    const isDraft = data.state === 'Draft';
    
    return (
      <Menu position="bottom-end" shadow="md" withArrow>
        <Menu.Target>
          <ActionIcon>
            <IconDotsVertical size={16} />
          </ActionIcon>
        </Menu.Target>
        
        <Menu.Dropdown sx={{ maxHeight: '250px', overflowY: 'auto' }}>
          <Menu.Item 
            icon={<IconEye size={14} />} 
            onClick={() => handleView(data)}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <FormattedMessage id="action.view" />
          </Menu.Item>
          
          {!isArchived && !isDraft && (
            <>
              <Menu.Item 
                icon={<IconTruckDelivery size={14} />} 
                onClick={() => handleTransport(data)}
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <FormattedMessage id="action.transport" />
              </Menu.Item>
              
              <Menu.Item 
                icon={<IconArrowsLeftRight size={14} />} 
                onClick={() => handleTransform(data)}
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <FormattedMessage id="action.transform" />
              </Menu.Item>
              
              <Menu.Item 
                icon={<IconPhoto size={14} />} 
                onClick={() => handleLoadImage(data)}
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <FormattedMessage id="action.loadImage" />
              </Menu.Item>
              
              <Menu.Item 
                color="orange"
                icon={<IconArchive size={14} />} 
                onClick={() => handleArchive(data)}
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <FormattedMessage id="action.archive" />
              </Menu.Item>
            </>
          )}
          
          {isDraft && (
            <Menu.Item 
              color="red"
              icon={<IconTrash size={14} />} 
              onClick={() => handleDelete(data)}
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <FormattedMessage id="action.delete" />
            </Menu.Item>
          )}
          
          {data.certificate && (
            <Menu.Item 
              icon={<IconDownload size={14} />} 
              onClick={() => downloadCertificate(data.productID)}
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <FormattedMessage id="action.downloadCertificate" />
            </Menu.Item>
          )}
        </Menu.Dropdown>
      </Menu>
    );
  };
  
  // Render table view
  const renderTableView = () => {
    const products = paginatedProducts();
    
    return (
      <ScrollArea>
        <Table striped highlightOnHover withBorder>
          <thead>
            <tr>
              <th><FormattedMessage id="table.product" /></th>
              <th><FormattedMessage id="table.sku" /></th>
              <th><FormattedMessage id="table.status" /></th>
              <th><FormattedMessage id="table.location" /></th>
              <th><FormattedMessage id="table.createdOn" /></th>
              <th><FormattedMessage id="table.certificate" /></th>
              <th style={{ width: 180 }}><FormattedMessage id="table.actions" /></th>
            </tr>
          </thead>
          <tbody>
            {products.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <Center p="xl">
                    <Text color="dimmed">
                      <FormattedMessage id={`table.noProducts.${activeTab}`} />
                    </Text>
                  </Center>
                </td>
              </tr>
            ) : (
              products.map((product) => (
                <tr key={product.productID}>
                  <td>
                    <Group spacing="sm">
                      {product.imagePath ? (
                        <Avatar 
                          src={product.imagePath} 
                          size="sm" 
                          radius="sm"
                        />
                      ) : (
                        <Avatar size="sm" radius="sm" color="blue">
                          {product.productName?.charAt(0) || 'P'}
                        </Avatar>
                      )}
                      <div>
                        <Text size="sm" weight={500}>
                          {product.productName}
                        </Text>
                        <Text size="xs" color="dimmed">
                          {product.description?.substring(0, 40) || '-'}
                          {product.description?.length > 40 ? '...' : ''}
                        </Text>
                      </div>
                    </Group>
                  </td>
                  <td>
                    <Text size="sm">{product.sku || '-'}</Text>
                  </td>
                  <td>
                    {renderStatusBadge(product)}
                  </td>
                  <td>
                    <Text size="sm">{product.location || '-'}</Text>
                  </td>
                  <td>
                    <Text size="sm">
                      {moment(product.createdOn).format('DD-MM-YYYY') || '-'}
                    </Text>
                  </td>
                  <td>
                    {product.certificate ? (
                      <Center>
                        <ActionIcon 
                          color="blue" 
                          onClick={() => downloadCertificate(product.productID)}
                        >
                          <FontAwesomeIcon icon={faFilePdf} />
                        </ActionIcon>
                      </Center>
                    ) : (
                      <Center>-</Center>
                    )}
                  </td>
                  <td>
                    <Group position="right" spacing="xs">
                      {renderPrimaryAction(product)}
                      <ActionIcon 
                        color="blue" 
                        onClick={() => handleView(product)}
                      >
                        <IconEye size={16} />
                      </ActionIcon>
                      {renderActionMenu(product)}
                    </Group>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </ScrollArea>
    );
  };

  // Add this new function inside your ImprovedProductManagement component
  const getImages = async (value) => {
    const imageUrl = `https://vertoledgerstorage.blob.core.windows.net/sqldbledgerdigests/images/${value.productID}.png`;
    const defaultImage= `https://vertoledgerstorage.blob.core.windows.net/sqldbledgerdigests/images/${value.product_name_id}.jpg`; 
    try {
      const response = await fetch(imageUrl);
      if (response.ok) {
        return imageUrl;
      } else {
        console.error("Error fetching image. Server returned:", response.status);
        return defaultImage;
      }
    } catch (error) {
      return defaultImage;
    }  
  };

  // Add a new state to store product images
  const [productImages, setProductImages] = useState({});

  // Add a function to load images for all products in the current view
  const loadProductImages = async (products) => {
    const imagePromises = products.map(async (product) => {
      const imageUrl = await getImages(product);
      return { productID: product.productID, imageUrl };
    });
    
    const loadedImages = await Promise.all(imagePromises);
    const imagesMap = {};
    loadedImages.forEach(item => {
      imagesMap[item.productID] = item.imageUrl;
    });
    
    setProductImages(imagesMap);
  };

  // Update the useEffect to load images when products change
  useEffect(() => {
    const products = paginatedProducts();
    if (products.length > 0) {
      loadProductImages(products);
    }
  }, [currentPage, search, activeTab, dataLot]);
  
  // Render card view
  const renderCardView = () => {
    const products = paginatedProducts();
    
    if (products.length === 0) {
      return (
        <Center p="xl">
          <Text color="dimmed">
            <FormattedMessage id={`table.noProducts.${activeTab}`} />
          </Text>
        </Center>
      );
    }
    
    return (
      <Grid>
        {products.map((product) => (
          <Grid.Col xs={12} sm={6} md={4} key={product.productID}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Card.Section>
              {productImages[product.productID] ? (
                  <div 
                    style={{ 
                      height: 140, 
                      backgroundImage: `url(${productImages[product.productID]})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                  />
                ) : product.imagePath ? (
                  <div 
                    style={{ 
                      height: 140, 
                      backgroundImage: `url(${product.imagePath})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                  />
                ) : (
                  <Center style={{ height: 140, backgroundColor: '#f8f9fa' }}>
                    <Avatar 
                      size={60} 
                      radius="md" 
                      color="blue" 
                      variant="filled"
                    >
                      {product.productName?.charAt(0).toUpperCase() || 'P'}
                    </Avatar>
                  </Center>
                )}
              </Card.Section>
              <Group position="apart" mt="md" mb="xs">
                <Text weight={500}>{product.productName}</Text>
                {renderStatusBadge(product)}
              </Group>
              
              <Text size="sm" color="dimmed" mb="md" lineClamp={2}>
                {product.description || '-'}
              </Text>
              <Group position="apart" spacing="xs">
                <Text size="xs">
                  <b><FormattedMessage id="card.sku" />:</b> {product.sku || '-'}
                </Text>
                <Text size="xs">
                  <b><FormattedMessage id="card.location" />:</b> {product.location || '-'}
                </Text>
              </Group>
              
              <Divider my="sm" />
              
              <Group position="apart">
                {renderPrimaryAction(product)}
                
                <Group spacing={5}>
                  {product.certificate && (
                    <Tooltip label={intl.formatMessage({ id: 'tooltip.certificate' })}>
                      <ActionIcon 
                        color="blue" 
                        onClick={() => downloadCertificate(product.productID)}
                      >
                        <FontAwesomeIcon icon={faFilePdf} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                  
                  <Tooltip label={intl.formatMessage({ id: 'tooltip.view' })}>
                    <ActionIcon 
                      color="blue" 
                      onClick={() => handleView(product)}
                    >
                      <IconEye size={16} />
                    </ActionIcon>
                  </Tooltip>
                  {renderActionMenu(product)}
              </Group>
              </Group>
            </Card>
          </Grid.Col>
        ))}
      </Grid>
    );
  };

  const renderSummary = () => {
    const totalProducts = dataLot.length;
    const activeProducts = dataLot.filter(p => p.state !== 'Draft' && p.publishId !== -1).length;
    const draftProducts = dataLot.filter(p => p.state === 'Draft').length;
    const archivedProducts = dataLot.filter(p => p.publishId === -1).length;
    const forSaleProducts = dataLot.filter(p => p.publish === 'Y').length;
    
    return (
      <Grid mb="md">
        <Grid.Col span={3}>
          <Paper p="md" withBorder>
            <Text size="lg" weight={700} color="blue">
              {totalProducts}
            </Text>
            <Text size="sm" color="dimmed">
              <FormattedMessage id="summary.totalProducts" />
            </Text>
          </Paper>
        </Grid.Col>
        <Grid.Col span={3}>
          <Paper p="md" withBorder>
            <Text size="lg" weight={700} color="green">
              {forSaleProducts}
            </Text>
            <Text size="sm" color="dimmed">
              <FormattedMessage id="summary.forSale" />
            </Text>
          </Paper>
        </Grid.Col>
        <Grid.Col span={3}>
          <Paper p="md" withBorder>
            <Text size="lg" weight={700} color="blue">
              {draftProducts}
            </Text>
            <Text size="sm" color="dimmed">
              <FormattedMessage id="summary.drafts" />
            </Text>
          </Paper>
        </Grid.Col>
        <Grid.Col span={3}>
          <Paper p="md" withBorder>
            <Text size="lg" weight={700} color="gray">
              {archivedProducts}
            </Text>
            <Text size="sm" color="dimmed">
              <FormattedMessage id="summary.archived" />
            </Text>
          </Paper>
        </Grid.Col>
      </Grid>
    );
  };
  
  return (
    <Box p="md">
      <Group position="apart" mb="lg">
        <Title order={2}>
          <FormattedMessage id="productManagement.title" />
        </Title>
        
        <Group>
          <Tooltip label={intl.formatMessage({ id: 'tooltip.refresh' })}>
            <ActionIcon
              color="blue"
              variant="subtle"
              onClick={handleRefresh}
            >
              <IconRefresh size={20} />
            </ActionIcon>
          </Tooltip>
          
          <Button
            leftIcon={<IconPlus size={16} />}
            onClick={() => setShowCreateModal(true)}
          >
            <FormattedMessage id="button.createProduct" />
          </Button>
        </Group>
      </Group>
      
      {renderSummary()}
      
      <Tabs value={activeTab} onTabChange={setActiveTab} mb="md">
        <Tabs.List>
          <Tabs.Tab 
            value={PRODUCT_STATES.ACTIVE} 
            icon={<IconFileText size={14} />}
          >
            <FormattedMessage id="tabs.activeProducts" />
          </Tabs.Tab>
          <Tabs.Tab 
            value={PRODUCT_STATES.DRAFT} 
            icon={<IconEdit size={14} />}
          >
            <FormattedMessage id="tabs.draftProducts" />
          </Tabs.Tab>
          <Tabs.Tab 
            value={PRODUCT_STATES.ARCHIVED} 
            icon={<IconArchive size={14} />}
          >
            <FormattedMessage id="tabs.archivedProducts" />
          </Tabs.Tab>
        </Tabs.List>
      </Tabs>
      
      <Paper mb="md" p="md" withBorder>
        <Group position="apart">
          <TextInput
            placeholder={intl.formatMessage({ id: 'search.placeholder' })}
            icon={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            style={{ width: '60%' }}
          />
          
          <Group>
            <Button 
              variant="subtle" 
              leftIcon={<IconFilter size={16} />}
              color="gray"
            >
              <FormattedMessage id="button.advancedFilters" />
            </Button>
            
            <SegmentedControl
              value={viewMode}
              onChange={setViewMode}
              data={[
                { 
                  value: VIEW_MODES.TABLE, 
                  label: <Center><IconLayoutList size={16} /></Center>
                },
                { 
                  value: VIEW_MODES.CARDS, 
                  label: <Center><IconLayoutGrid size={16} /></Center>
                }
              ]}
            />
          </Group>
        </Group>
      </Paper>
      
      {/* Content area */}
      <Paper p="md" withBorder>
        {viewMode === VIEW_MODES.TABLE ? renderTableView() : renderCardView()}
        
        {/* Pagination */}
        {getFilteredProducts().length > itemsPerPage && (
          <Center mt="md">
            <Pagination
              total={Math.ceil(getFilteredProducts().length / itemsPerPage)}
              value={currentPage}
              onChange={setCurrentPage}
              withEdges
            />
          </Center>
        )}
      </Paper>
      
      {/* Modals */}
      {selectedData && (
        <ProductPanel
          modal={showProductPanel}
          toggle={() => setShowProductPanel(false)}
          readonly={true}
          value={selectedData}
        />
      )}
      
      {transportData && (
        <TransportationModal
          modal={showTransportModal}
          toggle={() => setShowTransportModal(false)}
          title={
            transportMode === 'transform' 
              ? <FormattedMessage id="myprods.xtratitle.transform" />
              : <FormattedMessage id="myprods.xtratitle.transport" />
          }
          data={transportData}
          tag={transportMode === 'transform' ? 'TRANSFORM' : 'TRANSPORT'}
          dataLot={dataLot}
        />
      )}
      
      {selectedData && (
        <LoadImageModal
          modal={showLoadImageModal}
          toggle={() => setShowLoadImageModal(false)}
          selectedData={selectedData}
          onSave={(obj, toggle) => {
            setShowLoadImageModal(false);
          }}
        />
      )}
      
      {selectedData && (
        <RemoveFieldConfirmationModal
          fieldName={selectedData}
          onConfirm={() => {
            publishProduct(selectedData);
            setShowSellConfirmationModal(false);
          }}
          onCancel={() => setShowSellConfirmationModal(false)}
          opened={showSellConfirmationModal}
        />
      )}
      
      <CreatePropertiesModal
        modal={showCreateModal}
        toggle={() => setShowCreateModal(false)}
        onSave={(obj, cb) => onSave(obj, cb)}
        onDraftSave={(obj, cb) => onDraftSave(obj, cb)}
      />
      
      {/* Reusing RemoveFieldConfirmationModal for our confirmation dialogs */}
      <RemoveFieldConfirmationModal
        fieldName={{ name: <FormattedMessage id="modals.archive.title" /> }}
        message={<FormattedMessage id="modals.archive.message" />}
        onConfirm={confirmArchive}
        onCancel={() => setShowArchiveModal(false)}
        opened={showArchiveModal}
        confirmText={<FormattedMessage id="modals.archive.confirm" />}
        cancelText={<FormattedMessage id="modals.archive.cancel" />}
      />
      
      <RemoveFieldConfirmationModal
        fieldName={{ name: <FormattedMessage id="modals.unarchive.title" /> }}
        message={<FormattedMessage id="modals.unarchive.message" />}
        onConfirm={confirmUnarchive}
        onCancel={() => setShowUnarchiveModal(false)}
        opened={showUnarchiveModal}
        confirmText={<FormattedMessage id="modals.unarchive.confirm" />}
        cancelText={<FormattedMessage id="modals.unarchive.cancel" />}
      />
      
      <RemoveFieldConfirmationModal
        fieldName={{ name: <FormattedMessage id="modals.delete.title" /> }}
        message={<FormattedMessage id="modals.delete.message" />}
        onConfirm={confirmDelete}
        onCancel={() => setShowDeleteModal(false)}
        opened={showDeleteModal}
        confirmText={<FormattedMessage id="modals.delete.confirm" />}
        cancelText={<FormattedMessage id="modals.delete.cancel" />}
      />
    </Box>
  );
}